/**
 * Iter 67ep — Vendor "Risk signals" tile.
 *
 * Surfaces two halves of the same `/vendor/risk-signals` payload:
 *   • A 3-stat header strip (open rate, avg time-to-open, "% within 1h")
 *   • A collapsible at-risk list of confirmed bookings whose agreement
 *     WhatsApp was sent >48h ago but never opened, AND whose pickup is
 *     still in the future. Each row deep-links to the agreement +
 *     surfaces the renter's email so the vendor can fire a courtesy
 *     call before pickup day.
 *
 * Sits at the top of the vendor Bookings tab — it's an actionable list,
 * not a passive chart, so it earns the prime spot.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Activity, AlertTriangle, ChevronDown, ChevronUp, Mail,
  Clock, Loader2, ShieldCheck, FileText, Phone,
} from "lucide-react";

function _Stat({ label, value, sub, tone = "neutral", icon: Icon, testid }) {
  const tones = {
    emerald: "bg-emerald-50 ring-emerald-200 text-emerald-800",
    blue:    "bg-blue-50 ring-blue-200 text-blue-800",
    amber:   "bg-amber-50 ring-amber-200 text-amber-800",
    rose:    "bg-rose-50 ring-rose-200 text-rose-800",
    neutral: "bg-neutral-50 ring-neutral-200 text-neutral-800",
  };
  return (
    <div className={`rounded-2xl ring-1 p-3 ${tones[tone] || tones.neutral}`} data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold opacity-75">
        {Icon ? <Icon className="h-3 w-3" /> : null}
        {label}
      </div>
      <div className="text-2xl font-display font-medium leading-tight mt-0.5 tabular-nums">{value}</div>
      {sub && <div className="text-[11px] mt-0.5 opacity-70">{sub}</div>}
    </div>
  );
}

export default function VendorRiskSignalsTile() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/vendor/risk-signals");
      setData(d);
    } catch (e) {
      // Soft-fail: a vendor with zero bookings hasn't broken anything,
      // so we render the empty-state instead of an error toast.
      console.warn("[risk-signals]", formatApiError(e));
      setData({ metrics: { open_rate_pct: 0, at_risk_count: 0 }, at_risk: [] });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const m = data?.metrics || {};
  const atRisk = data?.at_risk || [];
  const hasData = (m.bookings_with_agreement ?? 0) > 0;

  // Open-rate tone: green ≥90%, blue ≥70%, amber ≥40%, rose otherwise.
  const openRateTone =
    m.open_rate_pct >= 90 ? "emerald" :
    m.open_rate_pct >= 70 ? "blue" :
    m.open_rate_pct >= 40 ? "amber" : "rose";

  return (
    <div className="rounded-2xl border border-border bg-white p-5 mb-6" data-testid="vendor-risk-signals">
      <header className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div>
          <h3 className="font-display text-lg font-medium tracking-tight flex items-center gap-2">
            <Activity className="h-4 w-4 text-violet-600" />
            Risk signals · last 90 days
          </h3>
          <p className="text-xs text-neutral-500 mt-0.5">
            How fast renters open the agreement you send on WhatsApp · who hasn't opened theirs yet.
          </p>
        </div>
        {atRisk.length > 0 && (
          <span
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-100 text-amber-800 border border-amber-200 text-[11px] font-bold"
            data-testid="risk-banner-count"
          >
            <AlertTriangle className="h-3 w-3" />
            {atRisk.length} need{atRisk.length === 1 ? "s" : ""} a courtesy call
          </span>
        )}
      </header>

      {loading ? (
        <div className="text-xs text-neutral-500 italic py-4 text-center">
          <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin inline" /> Crunching open-rate stats…
        </div>
      ) : !hasData ? (
        <div className="text-xs text-neutral-500 italic py-3 text-center bg-neutral-50/50 rounded-xl">
          You don't have any confirmed bookings yet (in the last 90 days) where the agreement was sent over WhatsApp.
          Stats will appear here once the first one lands.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5" data-testid="risk-signals-stats">
            <_Stat
              label="Open rate"
              value={`${m.open_rate_pct}%`}
              sub={`${m.bookings_opened}/${m.bookings_with_agreement} agreements opened`}
              tone={openRateTone}
              icon={ShieldCheck}
              testid="risk-stat-open-rate"
            />
            <_Stat
              label="Avg time to open"
              value={m.avg_open_human || "—"}
              sub={m.avg_open_minutes ? "Across all opens" : "No opens yet"}
              tone="blue"
              icon={Clock}
              testid="risk-stat-avg-open"
            />
            <_Stat
              label="Within 1 hour"
              value={`${m.within_1h_pct}%`}
              sub="Of those who opened"
              tone="emerald"
              icon={Clock}
              testid="risk-stat-within-1h"
            />
            <_Stat
              label="At-risk"
              value={atRisk.length}
              sub="Sent >48h ago · unopened"
              tone={atRisk.length === 0 ? "neutral" : "amber"}
              icon={AlertTriangle}
              testid="risk-stat-at-risk"
            />
          </div>

          {atRisk.length > 0 && (
            <div className="mt-4">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setExpanded((s) => !s)}
                className="rounded-full"
                data-testid="risk-toggle"
              >
                {expanded ? <ChevronUp className="h-3.5 w-3.5 mr-1.5" /> : <ChevronDown className="h-3.5 w-3.5 mr-1.5" />}
                {expanded ? "Hide" : "Show"} at-risk bookings ({atRisk.length})
              </Button>
              {expanded && (
                <div className="mt-3 rounded-2xl border border-amber-200 bg-amber-50/40 overflow-hidden" data-testid="risk-at-risk-list">
                  <table className="w-full text-xs">
                    <thead className="bg-amber-100/70 text-[10px] uppercase tracking-wider text-amber-900 font-bold">
                      <tr>
                        <th className="px-3 py-2 text-left">Renter</th>
                        <th className="px-3 py-2 text-left">Pickup</th>
                        <th className="px-3 py-2 text-left">Silent for</th>
                        <th className="px-3 py-2 text-left">Total</th>
                        <th className="px-3 py-2 text-left">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {atRisk.map((b) => (
                        <tr key={b.id} className="border-t border-amber-200" data-testid={`risk-row-${b.id}`}>
                          <td className="px-3 py-2">
                            <div className="font-semibold text-neutral-900">{b.customer_name || "—"}</div>
                            {b.customer_email && (
                              <a href={`mailto:${b.customer_email}`} className="text-[11px] text-blue-700 hover:underline inline-flex items-center gap-1 mt-0.5">
                                <Mail className="h-3 w-3" /> {b.customer_email}
                              </a>
                            )}
                          </td>
                          <td className="px-3 py-2">
                            <div className="font-semibold text-amber-900">
                              {b.days_to_pickup <= 1 ? "Tomorrow" : `in ${b.days_to_pickup} days`}
                            </div>
                            <div className="text-[10px] text-neutral-500">{(b.start_date || "").slice(0, 10)}</div>
                          </td>
                          <td className="px-3 py-2 font-mono text-amber-800">
                            {b.hours_silent < 48 ? `${Math.round(b.hours_silent)} h` : `${Math.round(b.hours_silent / 24)} d`}
                          </td>
                          <td className="px-3 py-2 tabular-nums">€{Number(b.amount || 0).toFixed(0)}</td>
                          <td className="px-3 py-2">
                            <div className="flex gap-1.5 flex-wrap">
                              <Link
                                to={`/agreements/${b.id}`}
                                className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white border border-amber-300 text-amber-900 text-[11px] font-semibold hover:bg-amber-100"
                                data-testid={`risk-open-agreement-${b.id}`}
                              >
                                <FileText className="h-3 w-3" /> Agreement
                              </Link>
                              {b.customer_email && (
                                <a
                                  href={`mailto:${b.customer_email}?subject=Quick check-in on your upcoming rental`}
                                  className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-blue-600 text-white text-[11px] font-semibold hover:bg-blue-700"
                                  data-testid={`risk-email-${b.id}`}
                                >
                                  <Phone className="h-3 w-3" /> Reach out
                                </a>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
